import { dateToCalendarStateIndex } from '@/helpers/calendarHelper';
import { dateStringToApiFormat, dateToApiFormat } from '@/helpers/dateHelper';
import { FixedLeaveTypeId } from '@/services/availability/availability.types';
import {
  AvailabilityException,
  RosteredEmployeeIdAndSchedule,
  WorkingPatternDay
} from '@/services/gql/graphql.generated';
import { DayOfWeekAndHoliday } from '@/services/holiday/holiday.types';
import { EmployeeSchedule, EmployeeScheduleDay } from './schedule.types';

export const buildSchedule =
  (
    dates: Date[],
    mapToLeaveTypeId: (leaveTypeId?: string) => string | undefined,
    getDayOfWeekAndHoliday?: (date: Date) => DayOfWeekAndHoliday
  ) =>
  (patterns: WorkingPatternDay[], exceptions: AvailabilityException[], shifts: RosteredEmployeeIdAndSchedule[]) => {
    const schedule: EmployeeSchedule = {};

    dates.forEach(d => {
      const dayInSchedule: EmployeeScheduleDay = { date: d, availableTimes: [], shifts: [] };
      schedule[dateToCalendarStateIndex(d)] = dayInSchedule;

      const dayOfWeekAndHoliday = getDayOfWeekAndHoliday ? getDayOfWeekAndHoliday(d) : d.getUTCDay();
      const pattern = patterns?.find(p => p.dayNumber === dayOfWeekAndHoliday);
      if (!pattern) {
        throw Error('Pattern list is incomplete.');
      }

      dayInSchedule.patternStart = pattern.startTime;
      dayInSchedule.patternEnd = pattern.endTime;

      if (!pattern.startTime && !pattern.endTime) {
        dayInSchedule.leaveType = FixedLeaveTypeId.ROSTER;
      } else if (exceptions) {
        const exceptionList = exceptions.filter(e => dateStringToApiFormat(e.date) === dateToApiFormat(d));

        if (exceptionList.length === 0) {
          dayInSchedule.availableTimes.push({ start: pattern.startTime, end: pattern.endTime });
        } else {
          dayInSchedule.availableTimes = exceptionList.map(e => ({ start: e.start, end: e.end }));
          const firstAbsence = exceptionList.find(e => !e.available);
          if (firstAbsence) {
            dayInSchedule.leaveType = firstAbsence.leaveType?.id
              ? mapToLeaveTypeId(firstAbsence.leaveType.id)
              : FixedLeaveTypeId.UNKNOWN;
          }
        }
      }

      const shiftForTheDay = shifts.filter(s => dateStringToApiFormat(s.date) === dateToApiFormat(d));
      if (shiftForTheDay.length > 0) {
        dayInSchedule.shifts = shiftForTheDay.map(s => ({
          start: s.start,
          end: s.end,
          tasks: (s.timeSlots ?? [])
            .filter(slot => {
              if (!slot.properties) return false;
              try {
                return JSON.parse(slot.properties).type === 'task';
              } catch {
                return false;
              }
            })
            .map(slot => ({
              start: slot.start,
              end: slot.end,
              taskType: slot.properties ? JSON.parse(slot.properties).context : undefined
            }))
        }));
      }
    });

    return schedule;
  };
